import styled from 'styled-components';
import { ArrowUp, ArrowDown, Loader } from 'lucide-react';
import { downloadAsExcel } from '../../../../utils/downloadAsExcel';
import InsightSummary from '../InsigntSummary';
import { formatCurrency } from '../../../../utils/Function';
import { useDragScroll } from '../../../../utils/useDragScroll';
import { s } from '../../components/PeriodStatsPage.styles';

export type MonthlyTrend = {
  startDate: string; // "YYYY-MM-DD"
  endDate: string; // "YYYY-MM-DD"
  claim_price: string | number; // 청구금액
  bid_rate: string | number; // 낙찰률(%)
  bid_price_rate: string | number; // 낙찰가율(%)
  collateral_value_rate: string | number; // 담보가치율(%)
  recovery_rate: string | number; // 회수율(%)
  loss_rate: string | number; // 손실률(%)
  mortgage_amount: number;
  shortfall_rate: number;
  progressed_items?: number | string;
  successful_items?: number | string;
  appraisal_price?: number | string;
  avg_bidder_count?: number | string;
};

interface ComparisonAnalysisProps {
  periodType: 'week' | 'month' | 'year';
  data: MonthlyTrend[] | null;
  loading?: boolean;
  error?: string | null;
  insights?: Record<string, string[]> | null; // { "긍정적 변화": string[], "우려 요인": string[] }
  insightsLoading?: boolean;
  insightsError?: string | null;
}

const num = (v: unknown) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  const n = Number(String(v).replace(/[,%]/g, ''));
  return isNaN(n) ? 0 : n;
};

type Row = {
  period: string; // "YYYY.MM" or "YYYY.MM ~ YYYY.MM"
  progressed_items: number; // 진행건수
  successful_items: number; // 낙찰건수
  bid_rate: number; // 낙찰률(%)
  bid_price_rate: number; // 낙찰가율(%)
  avg_bidder_count: number; // 평균 응찰자수
  appraisal_price: number; // 감정가액
  mortgage_amount: number; // 근저당액
  claim_price: number; // 청구금액
  collateral_value_rate: number; // 담보가치율(%)
  recovery_rate: number; // 회수율(%)
  loss_rate: number; // 손실률(%)
  shortfall_rate: number; // 회수부족율(%)
};

// 증감률 표시 컴포넌트
const ChangeIndicator = ({ value }: { value: string }) => {
  const isPositive = value.startsWith('+');
  const isNegative = value.startsWith('-');

  if (value == '-') {
    return;
  }

  if (isPositive)
    return (
      <Indicator positive>
        <ArrowUp size={20} /> {value.substring(1)}
      </Indicator>
    );

  if (isNegative)
    return (
      <Indicator negative>
        <ArrowDown size={20} /> {value.substring(1)}
      </Indicator>
    );

  return <span>{value}</span>;
};

const NationalNPLDelta = ({
  periodType,
  data,
  loading,
  error,
  insights,
  insightsLoading,
  insightsError,
}: ComparisonAnalysisProps) => {
  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  const sameMonth = (a?: string, b?: string) => {
    if (!a || !b) return false;
    const A = new Date(a);
    const B = new Date(b);
    return A.getFullYear() === B.getFullYear() && A.getMonth() === B.getMonth();
  };

  const parseYM = (iso?: string) => {
    if (!iso) return { y: NaN, m: NaN };
    const [y, m] = iso.split('-');
    return { y: Number(y), m: Number(m) };
  };

  const fmtYM = (y: number, m: number) =>
    isNaN(y) || isNaN(m) ? '' : `${y}.${String(m).padStart(2, '0')}`;

  const periodLabelFromRange = (startISO?: string, endISO?: string) => {
    const s = parseYM(startISO);
    const e = parseYM(endISO);
    if (isNaN(s.y) || isNaN(s.m) || isNaN(e.y) || isNaN(e.m)) return '';
    const sLabel = fmtYM(s.y, s.m);
    const eLabel = fmtYM(e.y, e.m);
    return s.y === e.y && s.m === e.m ? sLabel : `${sLabel} ~ ${eLabel}`;
  };

  const previousMonthLabelOf = (startISO?: string) => {
    const { y, m } = parseYM(startISO);
    if (isNaN(y) || isNaN(m)) return '';
    let py = y,
      pm = m - 1;
    if (pm === 0) {
      py -= 1;
      pm = 12;
    }
    return fmtYM(py, pm);
  };

  const prevRaw = data?.[0];
  const currRaw = data?.[1];

  // “한 달만 선택” 여부: 현재 기간의 start와 end가 같은 달이면 true
  const isSingleMonth = sameMonth(currRaw?.startDate, currRaw?.endDate);

  const baseData: Row | null = currRaw
    ? {
        period: periodLabelFromRange(currRaw.startDate, currRaw.endDate),

        progressed_items: num(currRaw.progressed_items),
        successful_items: num(currRaw.successful_items),
        bid_rate: num(currRaw.bid_rate),
        bid_price_rate: num(currRaw.bid_price_rate),
        avg_bidder_count: num(currRaw.avg_bidder_count),
        appraisal_price: num(currRaw.appraisal_price),
        mortgage_amount: num(currRaw.mortgage_amount),
        claim_price: num(currRaw.claim_price),
        collateral_value_rate: num(currRaw.collateral_value_rate),
        recovery_rate: num(currRaw.recovery_rate),
        loss_rate: num(currRaw.loss_rate),
        shortfall_rate: num(currRaw.shortfall_rate),
      }
    : null;

  const prevData: Row | null = prevRaw
    ? {
        period: isSingleMonth
          ? previousMonthLabelOf(currRaw?.startDate)
          : periodLabelFromRange(prevRaw.startDate, prevRaw.endDate),

        progressed_items: num(prevRaw.progressed_items),
        successful_items: num(prevRaw.successful_items),
        bid_rate: num(prevRaw.bid_rate),
        bid_price_rate: num(prevRaw.bid_price_rate),
        avg_bidder_count: num(prevRaw.avg_bidder_count),
        appraisal_price: num(prevRaw.appraisal_price),
        mortgage_amount: num(prevRaw.mortgage_amount),
        claim_price: num(prevRaw.claim_price),
        collateral_value_rate: num(prevRaw.collateral_value_rate),
        recovery_rate: num(prevRaw.recovery_rate),
        loss_rate: num(prevRaw.loss_rate),
        shortfall_rate: num(prevRaw.shortfall_rate),
      }
    : null;

  const metrics = [
    { key: 'progressed_items', label: '진행건수', unit: '' },
    { key: 'successful_items', label: '낙찰건수', unit: '' },
    { key: 'bid_rate', label: '낙찰률', unit: '%' },
    { key: 'bid_price_rate', label: '낙찰가율', unit: '%' },
    { key: 'avg_bidder_count', label: '평균 응찰자수', unit: '' },
    { key: 'appraisal_price', label: '감정가액', unit: '₩' }, // 금액
    { key: 'mortgage_amount', label: '근저당액', unit: '₩' },
    { key: 'claim_price', label: '청구금액', unit: '₩' },
    { key: 'collateral_value_rate', label: '담보가치율', unit: '%' },
    { key: 'recovery_rate', label: '회수율', unit: '%' },
    { key: 'loss_rate', label: '손실률', unit: '%' },
    { key: 'shortfall_rate', label: '회수부족율', unit: '%' },
  ] as const;

  const getChangeString = (current?: number, prev?: number) => {
    if (prev === undefined || prev === 0 || current === undefined) return '-';
    const diff = current - prev;
    const percent = ((diff / prev) * 100).toFixed(1);
    if (diff > 0) return `+${percent}%`;
    if (diff < 0) return `${percent}%`;
    return '0.0%';
  };

  const formatValue = (val: any, unit: string) => {
    if (val === undefined || val === null) return '-';

    if (unit === '%') {
      return (
        Number(val).toLocaleString(undefined, { maximumFractionDigits: 1 }) +
        '%'
      );
    }
    if (unit === '₩') {
      return formatCurrency(Number(val) || 0); // 기존 유틸 사용
    }
    // 건수/평균 응찰자수 등 일반 숫자
    return Number(val).toLocaleString(undefined, { maximumFractionDigits: 1 });
  };

  const buildRow = (label: string, data: Row | null) => {
    const row: Record<string, any> = { 구분: label, 기간: data?.period ?? '-' };
    metrics.forEach(({ key, label }) => {
      const v = data ? (data[key as keyof Row] as number) : undefined;
      row[label] = v ?? '-';
    });
    return row;
  };

  const getChangePercent = (cur?: number, prev?: number) => {
    if (prev === undefined || prev === 0 || cur === undefined) return '-';
    return (((cur - prev) / prev) * 100).toFixed(1) + '%';
  };

  const buildChangeRow = (label: string, cur: Row | null, prev: Row | null) => {
    const row: Record<string, any> = { 구분: label, 기간: '-' };
    metrics.forEach(({ key, label }) => {
      const curVal = cur ? (cur[key as keyof Row] as number) : undefined;
      const prevVal = prev ? (prev[key as keyof Row] as number) : undefined;
      row[label] = getChangePercent(curVal, prevVal);
    });
    return row;
  };

  const handleDownloadExcel = () => {
    const rows = [
      buildRow('이전 기간', prevData),
      buildRow('현재 기간', baseData),
      buildChangeRow('현재 기간 증감률', baseData, prevData),
    ];
    downloadAsExcel(rows as any[], '전국_NPL_지표_증감률_분석');
  };

  const buildInsightText = (arr?: string[]) => {
    if (insightsLoading) return '인사이트를 불러오는 중…';
    if (insightsError) return insightsError;
    if (!arr || arr.length === 0) return '표시할 인사이트가 없어요.';
    return arr.join('\n\n');
  };

  const positiveText = buildInsightText(insights?.['긍정적 변화']);
  const negativeText = buildInsightText(insights?.['우려 요인']);

  return (
    <s.Wrap>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#e4edfd">
            <img src="/assets/images/icon/blue_clock.svg" alt="" />
          </s.Img>
          <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
            <img src="/assets/images/icon/download-black.svg" alt="" />
          </s.ExcelButton>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>전국 경매 지표 증감률 분석</h3>
          <p>전 기간 대비 증감률 분석</p>
        </s.TitleBox>
      </s.Header>

      {loading ? (
        <LoaderWrap>
          <LoaderImg
            src="/assets/images/icon/loader.gif"
            alt="전국 경매 지표"
          />
        </LoaderWrap>
      ) : (
        <>
          <TableContainer
            ref={ref}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
          >
            <Table>
              <thead>
                <tr>
                  <Th>기간</Th>
                  {metrics.map(({ label }) => (
                    <Th align="right" key={label}>
                      {label}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: '이전 기간', data: prevData },
                  { label: '현재 기간', data: baseData },
                ].map(({ label, data }) => (
                  <tr key={label}>
                    <Td style={{ color: '#787d87' }}>
                      {label}{' '}
                      <span style={{ color: '#424854', marginLeft: 10 }}>
                        {data?.period ?? '-'}
                      </span>
                    </Td>
                    {metrics.map(({ key, label, unit }) => {
                      const raw = data
                        ? (data[key as keyof Row] as number)
                        : undefined;
                      return (
                        <Td align="right" key={label}>
                          {data ? formatValue(raw, unit) : '-'}
                        </Td>
                      );
                    })}
                  </tr>
                ))}

                <AvgTr>
                  <TdAvg>현재 기간 증감률</TdAvg>
                  {metrics.map(({ key, label }) => (
                    <TdAvg key={label}>
                      <ChangeIndicator
                        value={getChangeString(
                          baseData
                            ? (baseData[key as keyof Row] as number)
                            : undefined,
                          prevData
                            ? (prevData[key as keyof Row] as number)
                            : undefined,
                        )}
                      />
                    </TdAvg>
                  ))}
                </AvgTr>
              </tbody>
            </Table>
          </TableContainer>

          <InsightSummary
            title="긍정적 변화"
            content={positiveText}
            titleColor="#0C6146"
            contentColor="#424854"
            bg="#DCF7E8"
          />
          <InsightSummary
            title="우려 요인"
            content={negativeText}
            titleColor="#A01B3C"
            contentColor="#424854"
            bg="#FEEFEF"
          />
        </>
      )}
    </s.Wrap>
  );
};

export default NationalNPLDelta;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 20px;
  height: 20px;
  display: inline-block;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  cursor: grab;

  &.grabbing {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`;

const Table = styled.table`
  min-width: max(760px, 100%);
  table-layout: auto;
  border-collapse: separate;
  border-spacing: 0;
  white-space: nowrap;

  thead th {
    background: #f4f5f7;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  thead th:first-child {
    border-top-left-radius: 8px;
  }

  thead th:last-child {
    border-top-right-radius: 8px;
  }

  tbody tr:not(:last-child) td {
    border-bottom: 1px solid #e8e9ed;
  }
`;

const Th = styled.th<{ width?: string; align?: 'left' | 'center' | 'right' }>`
  padding: 14px;
  font-size: 13px;
  font-weight: 500;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
  width: ${(p) => p.width ?? 'auto'};
`;

const Td = styled.td<{ align?: 'left' | 'center' | 'right' }>`
  padding: 14px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
  vertical-align: top;
`;

const AvgTr = styled.tr`
  background: #f6f9fe;
  width: 100%;
`;

const TdAvg = styled(Td)`
  font-weight: 700;
  font-size: 15px;
  color: #0c0e12;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  text-align: right;
`;

const Indicator = styled.span<{ positive?: boolean; negative?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  font-weight: 700;
  color: ${(p) => (p.positive ? '#0B7A58' : p.negative ? '#D0244B' : '#000')};
`;
